import { factionIcon } from "@/lib/game";
import { Check } from "lucide-react";

/**
 * WTC offer->choose chain. Locked rounds (already resolved) render solid with a check;
 * predicted rounds render dashed/translucent. Boxes are coloured by matchup outcome for
 * us: green = we win, red = opponent wins.
 * Props: routeMode (all solid, no checks — for hypothetical routes), scale (size multiplier).
 */
export default function PairingSequence({ sequence, youAre = "A", teamAName = "Team A", teamBName = "Team B", routeMode = false, scale = 1, showLegend = true }) {
  if (!Array.isArray(sequence) || sequence.length < 2 || sequence[0]?.kind !== "designate") return null;

  const W = Math.round(158 * scale), H = Math.round(58 * scale), CGAP = Math.round(42 * scale), RGAP = Math.round(16 * scale);
  const colX = (c) => c * (W + CGAP);
  const rowY = (r) => r * (H + RGAP);

  const boxes = [];
  const links = [];
  const winByKey = {};
  const lockByKey = {};
  const des = sequence[0];
  const desKey = `d-${des.pending}`;
  boxes.push({ key: desKey, col: 0, row: 0.5, player: des.pending_player });
  lockByKey[desKey] = !!des.locked;
  let prevPending = desKey;
  let col = 1;

  for (let i = 1; i < sequence.length; i++) {
    const s = sequence[i];
    const win = s.our_win ?? 0;
    if (s.forced) {
      const k = `c${col}-${s.chosen}`;
      boxes.push({ key: k, col, row: 0.5, player: s.chosen_player });
      links.push({ from: prevPending, to: k, win, locked: !!s.locked });
      winByKey[prevPending] = win; winByKey[k] = win; lockByKey[k] = !!s.locked;
    } else {
      const [id0, id1] = s.offered;
      const [p0, p1] = s.offered_players || [];
      const k0 = `c${col}-${id0}`, k1 = `c${col}-${id1}`;
      boxes.push({ key: k0, col, row: 0, player: p0 });
      boxes.push({ key: k1, col, row: 1, player: p1 });
      const chosenKey = s.chosen === id0 ? k0 : k1;
      const leftoverKey = s.chosen === id0 ? k1 : k0;
      links.push({ from: prevPending, to: chosenKey, win, locked: !!s.locked });
      winByKey[prevPending] = win; winByKey[chosenKey] = win;
      lockByKey[k0] = !!s.locked; lockByKey[k1] = !!s.locked;
      prevPending = leftoverKey;
    }
    col++;
  }

  const byKey = Object.fromEntries(boxes.map((b) => [b.key, b]));
  const totalW = colX(col - 1) + W;
  const totalH = 2 * H + RGAP;
  const cx = (b, side) => (side === "r" ? colX(b.col) + W : colX(b.col));
  const cy = (b) => rowY(b.row) + H / 2;
  const teamName = (t) => (t === "A" ? teamAName : teamBName);

  return (
    <div className="overflow-x-auto pb-2" data-testid="pairing-sequence">
      {showLegend && !routeMode && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2 text-[11px] text-slate-400">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded" style={{ background: "#15803d" }} /> win for you</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded" style={{ background: "#b91c1c" }} /> win for opponent</span>
          <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-slate-300" /> locked</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-dashed border-slate-500" /> projected</span>
        </div>
      )}
      <div className="relative" style={{ width: totalW, height: totalH, minWidth: totalW }}>
        <svg className="absolute inset-0" width={totalW} height={totalH} style={{ pointerEvents: "none" }}>
          {links.map((l, i) => {
            const a = byKey[l.from], b = byKey[l.to];
            if (!a || !b) return null;
            const x1 = cx(a, "r"), y1 = cy(a), x2 = cx(b, "l"), y2 = cy(b);
            const mx = (x1 + x2) / 2;
            const solid = routeMode || l.locked;
            return <path key={`${l.from}-${l.to}`} d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
              stroke={l.win > 0 ? "#16a34a" : "#dc2626"} strokeWidth="2.5" fill="none"
              opacity={solid ? 0.95 : 0.5} strokeDasharray={solid ? "0" : "5 4"} />;
          })}
        </svg>
        {boxes.map((b) => {
          const win = winByKey[b.key] ?? 0;
          const green = win > 0;
          const solid = routeMode || lockByKey[b.key];
          return (
            <div key={b.key} data-testid="sequence-box" data-locked={lockByKey[b.key] ? "1" : "0"}
              className="absolute rounded-lg border flex flex-col justify-center px-2.5 shadow-md"
              style={{
                left: colX(b.col), top: rowY(b.row), width: W, height: H,
                background: green ? "#15803d" : "#b91c1c",
                borderColor: green ? "#22c55e" : "#ef4444",
                borderStyle: solid ? "solid" : "dashed",
                opacity: solid ? 1 : 0.72,
                color: "#fff",
              }}>
              <div className="flex items-center gap-1.5">
                {b.player?.faction && (
                  <img src={factionIcon(b.player.faction)} alt="" className="h-5 w-5 object-contain shrink-0"
                    onError={(e) => (e.currentTarget.style.display = "none")} />
                )}
                <span className="text-xs font-semibold leading-tight truncate flex-1">{b.player?.name || "—"}</span>
                {!routeMode && lockByKey[b.key] && <Check className="h-3.5 w-3.5 text-white/90 shrink-0" />}
              </div>
              <span className="text-[9px] text-white/70 leading-tight truncate pl-0.5">{teamName(b.player?.team)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
